import React, { useRef, useState } from 'react';
import useBriteVerify from '../../../hooks/useBriteVerify';
import SessionStorage from '../../../utils/SessionStorage';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { TBlurEvent, TChangeEvent, TFocusEvent, TReactParseable } from '../../../utils/general-types';
import SlideDown from '../../SlideDown/SlideDown';
import { generateFieldErrorMessage } from '../../../utils/error-message';
import InputPhone from './component/InputPhone';

export interface IPhoneNumberFieldProps {
    label?: string | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactElement<any, string | React.JSXElementConstructor<any>>[];
    inputClass?: string;
    value: string;
    setValueCallback?: THandlerFunction;
    setErrorList?: Function;
    error?: boolean;
    alternate?: boolean;
    onFocusCallback?: TFocusEvent;
    onBlurCallback?: TBlurEvent;
    onChangeCallback?: TChangeEvent;
    disabled?: boolean;
    ref?: React.MutableRefObject<any>;
    valid?: [boolean, (arg?: any) => void];
    useSuccessIcon?: boolean;
    showSuccessIcon?: boolean;
    removeAsterisk?: boolean;
    required?: boolean;
    disableInternalValidation?: boolean;
    helperText?: string;
    disclaimerMessage?: TReactParseable;
    disclaimerClassName?: string;
    advancedMasking?: boolean;
}

const PhoneNumberField = (props: IPhoneNumberFieldProps & React.HTMLAttributes<HTMLInputElement>) => {
    const { phoneValidationApi } = useBriteVerify();
    const inputBaseRef = useRef<HTMLInputElement|null>(props.ref?.current ?? null);

    const [isInternallyValid, setIsInternallyValid] = useState(true);
    const [showIcon, setShowIcon] = useState(false);

    const [isBlurred, setIsBlurred] = useState(true);
    const [lastPhone, setLastPhone] = useState<{ value: string, result: boolean, validationResult?: any }>({ value: '', result: false });

    const [isFieldValid, setIsFieldValid] = props.valid ?? [isInternallyValid, setIsInternallyValid];

    const [plist] = useState<String[]>(PathUtils.getMetaPropertyValue('cq:plist')?.split(',').map(phone => atob(phone)) ?? []);

    const [fieldErrorMessage, setFieldErrorMessage] = useState<string>(generateFieldErrorMessage('Phone Number', 'missingValue'));
    
    const formatPhoneNumber = (phone?: string) => {
        if (!phone) {
            return '';
        }

        const match = phone.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return match[1] + '-' + match[2] + '-' + match[3];
        }

        if (isBlurred) {
            phone = phone.trim();
        }

        return phone;
    };

    const validatePhone = async () => {
        if (props.value?.replaceAll('-', '').length < 10 || plist?.includes(props.value?.replaceAll('-', ''))) {
            setFieldErrorMessage(generateFieldErrorMessage('Phone Number', !props.value ? 'missingValue' : undefined));
            return false;
        }
        const isAuthor = ['4502', '3000'].some((entry) => window.location.port.includes(entry));
        if (
            (!SessionStorage.contains('adobeMode') && !isAuthor) || 
            (SessionStorage.contains('adobeMode') && SessionStorage.contains('usePhoneApi'))
        ) {
            if (props.value === lastPhone.value) {
                if (lastPhone?.validationResult) {
                    SessionStorage.set('bvrslt', btoa(JSON.stringify(lastPhone.validationResult)));
                    setFieldErrorMessage(generateFieldErrorMessage('Phone Number', 'missingValue'));
                } else {
                    setFieldErrorMessage(generateFieldErrorMessage('Phone Number', 'thirdParty', 'Number'));
                }
                setLastPhone({ value: props.value, result: lastPhone.result, validationResult: lastPhone.validationResult });
                return lastPhone.result;
            }
            const validationResult = await phoneValidationApi(props.value);
            if (validationResult) {
                SessionStorage.set('bvrslt', btoa(JSON.stringify(validationResult)));
                setFieldErrorMessage(generateFieldErrorMessage('Phone Number', 'missingValue'));
            } else {
                setFieldErrorMessage(generateFieldErrorMessage('Phone Number', 'thirdParty', 'Number'));
            }
            setLastPhone({ value: props.value, result: !!validationResult, validationResult: validationResult });
            return !!validationResult;
        }
        setLastPhone({ value: props.value, result: true });
        return true;
    };

    const handleFormatValue = (newVal: string) => {
        props.setValueCallback && props.setValueCallback(formatPhoneNumber(newVal));
    };

    const handleSuccessIcon = async () => {
        const validation = await validatePhone();
        if (!props.disableInternalValidation) {
            setIsFieldValid(validation);
        }
        
        setShowIcon(validation);
        if (props.setErrorList) {
            props.setErrorList('phoneNumber', false, { value: formatPhoneNumber(props.value), valid: validation }, 'PrimaryPhoneNumber');
        }

    };
    
    const focusEventHandler = (e: React.FocusEvent<HTMLInputElement>) => {
        if (e.type === 'blur') {
            handleFormatValue(props.value);
            handleSuccessIcon();
            props.onBlurCallback && props.onBlurCallback(e);

        } else if (e.type === 'focus') {
            !props.disableInternalValidation && setIsFieldValid(true);
            setShowIcon(false);
            props.onFocusCallback && props.onFocusCallback(e);
            setIsBlurred(false);
        }
    };

    const onChangeEventHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        props.onChangeCallback && props.onChangeCallback(e);
    };

    return (
        <div onBlur={(e) => {
            const currentTarget = e.currentTarget;
            const relatedTarget = e.relatedTarget;

            if (!currentTarget.contains(relatedTarget as Node)) {
                setIsBlurred(true);
            }
        }}>
            <InputPhone
                value={props.value}
                label={props.label}
                id='phoneNumber'
                inputClass={props.inputClass}
                ref={inputBaseRef}
                setValue={handleFormatValue}
                valid={[isFieldValid, setIsFieldValid]}
                error={props.error || !isFieldValid}
                errorText={fieldErrorMessage}
                invalidErrorMessage={fieldErrorMessage}
                requiredMessage={fieldErrorMessage}
                required={true}
                onFocus={focusEventHandler}
                onBlur={focusEventHandler}
                onChange={onChangeEventHandler}
                alternate
                advancedMasking={props.advancedMasking}
                useSuccessIcon={props.useSuccessIcon}
                showExclamationError
                showSuccessIcon={props.showSuccessIcon !== undefined ? props.showSuccessIcon : showIcon}
                helperText={props.helperText}
            />
            {props.disclaimerMessage &&
                <SlideDown 
                    open={!isBlurred}
                    className={props.disclaimerClassName} 
                    tabIndex={-1}
                    >
                        {props.disclaimerMessage}
                </SlideDown>
            }
        </div>
    );
};

export default PhoneNumberField;